import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Btn, Card, Label, Input, Textarea } from "@/components/shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { GraduationCap, Plus, Pencil, Trash2, ChevronRight, CheckSquare, ArrowLeft, Languages, PlayCircle } from "lucide-react";
import { MediaManager } from "@/components/MediaManager";
import { GuideUploader } from "@/components/GuideUploader";
import { AppWalkthrough } from "@/components/AppWalkthrough";
import { fileUrl } from "@/lib/api";

const blank = { title: "", category: "General", summary: "", content: "", media: [] };

// sections <-> plain text ("# Heading" lines start a section; other non-empty lines are points)
const toText = (sections = []) => sections.map((s) => `# ${s.heading}\n${(s.points || []).join("\n")}`).join("\n\n");
const toSections = (text = "") => {
  const out = []; let cur = null;
  text.split("\n").forEach((raw) => {
    const line = raw.trim();
    if (!line) return;
    if (line.startsWith("#")) { cur = { heading: line.replace(/^#+\s*/, ""), points: [] }; out.push(cur); }
    else { if (!cur) { cur = { heading: "Overview", points: [] }; out.push(cur); } cur.points.push(line.replace(/^[-*]\s*/, "")); }
  });
  return out;
};

export default function Training() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const es = (i18n.language || "").startsWith("es");
  const loc = (g) => (es && g?.title_es
    ? { title: g.title_es, summary: g.summary_es || g.summary, sections: g.sections_es || g.sections }
    : { title: g.title, summary: g.summary, sections: g.sections });
  const isAdmin = user?.role === "admin";
  const [guides, setGuides] = useState([]);
  const [active, setActive] = useState(null);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [walk, setWalk] = useState(false);

  const load = useCallback(() => api.get("/training").then((r) => setGuides(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const translateAll = async () => {
    setTranslating(true);
    try { await api.post("/training/translate-missing"); toast.success("Spanish translations updated"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setTranslating(false); }
  };

  const openNew = () => setEdit({ ...blank });
  const openEdit = (g) => setEdit({ id: g.id, title: g.title, category: g.category || "General", summary: g.summary || "", content: toText(g.sections), media: g.media || [] });

  const save = async () => {
    if (!edit.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    const payload = { title: edit.title.trim(), category: edit.category.trim() || "General", summary: edit.summary.trim(), sections: toSections(edit.content), media: edit.media || [] };
    try {
      if (edit.id) await api.put(`/training/${edit.id}`, payload);
      else await api.post("/training", payload);
      toast.success("Training module saved"); setEdit(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (g) => {
    if (!window.confirm(`Delete "${g.title}"?`)) return;
    try { await api.delete(`/training/${g.id}`); toast.success("Deleted"); if (active?.id === g.id) setActive(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (active) {
    const A = loc(active);
    return (
      <div className="space-y-6 max-w-3xl">
        <button data-testid="training-back" onClick={() => setActive(null)} className="flex items-center gap-1 text-zinc-400 hover:text-[#FF5722] font-mono text-xs uppercase tracking-widest"><ArrowLeft size={14} /> {t("training.allTraining")}</button>
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-[#FF5722] mb-1">{active.category}</div>
          <h1 className="font-head font-black uppercase text-3xl sm:text-4xl text-white tracking-tight">{A.title}</h1>
          {A.summary && <p className="text-zinc-400 mt-2">{A.summary}</p>}
        </div>
        {(active.media || []).length > 0 && (
          <div className="grid gap-4 sm:grid-cols-2" data-testid="training-media">
            {active.media.map((m) => (
              <div key={m.id} className="border-2 border-zinc-800 bg-black/40 p-2" data-testid={`training-media-${m.id}`}>
                {m.kind === "image" && <img src={fileUrl(m.id)} alt={m.name} className="w-full object-contain max-h-80" />}
                {m.kind === "video" && <video src={fileUrl(m.id)} controls className="w-full max-h-80" data-testid={`training-video-${m.id}`} />}
                {m.kind === "document" && <a href={fileUrl(m.id)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-3 text-[#FF5722] hover:underline font-mono text-sm" data-testid={`training-doc-${m.id}`}>⬇ {m.name}</a>}
              </div>
            ))}
          </div>
        )}
        {(A.sections || []).map((s, i) => (
          <Card key={i} className="p-5" data-testid={`training-section-${i}`}>
            <div className="font-head font-bold text-white uppercase tracking-wide mb-3">{s.heading}</div>
            <ul className="space-y-2">
              {(s.points || []).map((p, j) => (
                <li key={j} className="flex items-start gap-2 text-sm text-zinc-300">
                  <CheckSquare size={15} className="text-[#FF5722] mt-0.5 shrink-0" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4 animate-rise">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-[#FF5722] mb-1">{t("training.academy")}</div>
          <h1 className="font-head font-black uppercase text-3xl sm:text-4xl text-white tracking-tight">{t("training.title")}</h1>
          <p className="text-zinc-500 text-sm mt-1 font-mono">{t("training.subtitle")}</p>
        </div>
        {isAdmin && <div className="flex gap-2 flex-wrap">
          <Btn data-testid="walkthrough-btn" variant="secondary" onClick={() => setWalk(true)}><PlayCircle size={16} /> How to use the app</Btn>
          <Btn data-testid="translate-all-btn" variant="secondary" disabled={translating} onClick={translateAll}><Languages size={16} /> {translating ? t("common.saving") : t("training.translateAll")}</Btn>
          <Btn data-testid="add-training-btn" onClick={openNew}><Plus size={16} /> {t("training.addModule")}</Btn>
        </div>}
        {!isAdmin && <Btn data-testid="walkthrough-btn" variant="secondary" onClick={() => setWalk(true)}><PlayCircle size={16} /> How to use the app</Btn>}
      </div>

      <AppWalkthrough open={walk} onClose={() => setWalk(false)} />
      {isAdmin && <GuideUploader />}

      {guides.length === 0 ? (
        <Card className="p-10 text-center text-zinc-600 font-mono"><GraduationCap className="inline mr-2" size={16} />{t("training.noModules")}</Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {guides.map((g, i) => { const G = loc(g); return (
            <Card key={g.id} data-testid={`training-${g.id}`} className="p-5 flex flex-col animate-rise" style={{ animationDelay: `${i * 40}ms` }}>
              <div className="w-11 h-11 bg-[#FF5722] text-black border-2 border-black shadow-[2px_2px_0px_#000] flex items-center justify-center mb-3"><GraduationCap size={20} /></div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF5722]">{g.category}</div>
              <div className="font-head font-bold text-white text-lg leading-tight mt-0.5">{G.title}</div>
              <p className="text-zinc-400 text-sm mt-2 flex-1">{G.summary}</p>
              <div className="mt-4 pt-3 border-t border-zinc-800 flex gap-2">
                <Btn data-testid={`open-training-${g.id}`} onClick={() => setActive(g)} className="flex-1">{t("common.start")} <ChevronRight size={14} /></Btn>
                {isAdmin && <Btn variant="secondary" data-testid={`edit-training-${g.id}`} onClick={() => openEdit(g)}><Pencil size={14} /></Btn>}
                {isAdmin && <Btn variant="danger" data-testid={`del-training-${g.id}`} onClick={() => del(g)}><Trash2 size={14} /></Btn>}
              </div>
            </Card>
          ); })}
        </div>
      )}

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-xl" data-testid="training-editor">
          <DialogHeader><DialogTitle className="font-head uppercase">{edit?.id ? "Edit" : "New"} Training Module</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Title</Label><Input data-testid="training-title" value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} /></div>
                <div><Label>Category</Label><Input data-testid="training-category" value={edit.category} onChange={(e) => setEdit({ ...edit, category: e.target.value })} /></div>
              </div>
              <div><Label>Summary</Label><Input data-testid="training-summary" value={edit.summary} onChange={(e) => setEdit({ ...edit, summary: e.target.value })} /></div>
              <div>
                <Label>Content</Label>
                <Textarea data-testid="training-content" rows={12} value={edit.content} onChange={(e) => setEdit({ ...edit, content: e.target.value })}
                  placeholder={"# Section heading\nStep or spec on its own line\nAnother step\n\n# Next section\n..."} />
                <p className="font-mono text-[10px] text-zinc-600 mt-1">Start a section with "# ". Each other line becomes a checklist point.</p>
              </div>
              <div>
                <Label>Photos, videos &amp; documents (shown to contractors)</Label>
                <MediaManager value={edit.media || []} onChange={(media) => setEdit({ ...edit, media })} testid="training-media-mgr" />
              </div>
              <div className="flex gap-2 justify-end">
                <Btn variant="secondary" onClick={() => setEdit(null)}>Cancel</Btn>
                <Btn data-testid="training-save" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save"}</Btn>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
